import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import PlatformToggle from './components/PlatformToggle';
import InstallSteps from './components/InstallSteps';
import InstallBenefits from './components/InstallBenefits';
import InstallFAQ from './components/InstallFAQ';
import InstallStatus from './components/InstallStatus';
import QRCodeSection from './components/QRCodeSection';

const PWAInstallInstructions = () => {
  const [detectedPlatform, setDetectedPlatform] = useState('ios');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent?.toLowerCase();
    const isIOS = /iphone|ipad|ipod/?.test(ua);
    const isAndroid = /android/?.test(ua);
    const detected = isAndroid ? 'android' : 'ios';
    setDetectedPlatform(detected);
    setSelectedPlatform(detected);

    // Check if already installed
    const isStandalone =
      window.matchMedia('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true;
    setIsInstalled(isStandalone);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e?.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt?.prompt();
    const { outcome } = await deferredPrompt?.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const activePlatform = selectedPlatform || detectedPlatform;

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Install ConstructionPro</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Add to your home screen for a native app experience
          </p>
        </div>

        {/* Install Status */}
        <InstallStatus isInstalled={isInstalled} />

        {/* QR Codes */}
        <QRCodeSection />

        {/* Platform Toggle */}
        <PlatformToggle
          activePlatform={activePlatform}
          detectedPlatform={detectedPlatform}
          onSelect={setSelectedPlatform}
        />

        {/* Install Steps */}
        <InstallSteps platform={activePlatform} />

        {/* Native Install Button (Android/Chrome) */}
        {deferredPrompt && activePlatform === 'android' && (
          <div className="bg-card border border-border rounded-xl p-5 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Your browser supports one-tap installation
            </p>
            <button
              onClick={handleNativeInstall}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Install App Now
            </button>
          </div>
        )}

        {/* Benefits */}
        <InstallBenefits />

        {/* FAQ */}
        <InstallFAQ />
      </div>
    </MainLayout>
  );
};

export default PWAInstallInstructions;
